import { Row, Col, Form } from "react-bootstrap";
import { UPLOADS_BASE } from "../constants/api";
import {
  filterCitiesForParent,
  filterCountries,
  findLocationTypeId,
} from "../../../utils/locationHelpers";
import { filterProceduresForSpecialty } from "../utils/medicalProcedureHelpers";

const DocSectionCard = ({ title, sectionNumber, children }) => (
  <div className="em-section-card">
    {title && (
      <h4 className="em-section-title">
        {sectionNumber != null && <span className="em-section-num">{sectionNumber}. </span>}
        {title}
      </h4>
    )}
    {children}
  </div>
);

const DocPhoneList = ({ formData, onChange }) => {
  const phones = (() => {
    if (Array.isArray(formData.phone_numbers) && formData.phone_numbers.length > 0) {
      return formData.phone_numbers;
    }
    const initial = [formData.phone_number_1 || "", formData.phone_number_2 || ""].filter(
      (p, i) => i === 0 || p
    );
    return initial.length ? initial : [""];
  })();

  const syncPhones = (next) => {
    onChange("phone_numbers", next);
    onChange("phone_number_1", next[0] || "");
    onChange("phone_number_2", next[1] || "");
  };

  return (
    <div className="em-phone-list">
      {phones.map((phone, idx) => (
        <div key={idx} className="em-phone-row">
          <Form.Control
            type="text"
            placeholder="Phone number"
            value={phone}
            onChange={(e) => {
              const next = [...phones];
              next[idx] = e.target.value;
              syncPhones(next);
            }}
          />
          {phones.length > 1 && (
            <button
              type="button"
              className="em-btn-remove"
              onClick={() => syncPhones(phones.filter((_, i) => i !== idx))}
              aria-label="Remove phone"
            >
              ×
            </button>
          )}
        </div>
      ))}
      <button type="button" className="em-btn-add" onClick={() => syncPhones([...phones, ""])}>
        + Add more
      </button>
    </div>
  );
};

const DoctorProfileForm = ({
  formData,
  onChange,
  formMode = "full",
  userTypes = [],
  establishments = [],
  locationTypes = [],
  locations = [],
  specialties = [],
  medicalProcedures = [],
  specializations = [],
  departments = [],
  designations = [],
  degrees = [],
  accreditations = [],
}) => {
  const stateTypeId = findLocationTypeId(locationTypes, "state", "province");
  const cityTypeId = findLocationTypeId(locationTypes, "city", "district");

  const countries = filterCountries(locations, locationTypes);
  const countryLocations = filterCitiesForParent(locations, locationTypes, formData.country_id);
  const states = locations.filter(
    (l) =>
      String(l.location_type_id) === String(stateTypeId) &&
      String(l.parent_location_id) === String(formData.country_id)
  );
  const cityParentId = formData.state_id || formData.country_id;
  const cities = locations.filter(
    (l) =>
      String(l.location_type_id) === String(cityTypeId) &&
      cityParentId &&
      String(l.parent_location_id) === String(cityParentId)
  );

  const selectedSpecialty = specialties.find(
    (s) => String(s.medical_specialty_id) === String(formData.medical_specialty_id)
  );

  const filteredProcedures = formData.medical_specialty_id
    ? filterProceduresForSpecialty(
        medicalProcedures,
        formData.medical_specialty_id,
        selectedSpecialty
      )
    : [];

  const selectedProcIds = formData.procedure_ids
    ? formData.procedure_ids.split(",").map((s) => s.trim())
    : [];
  const selectedSpecIds = formData.specialization_ids
    ? formData.specialization_ids.split(",").map((s) => s.trim())
    : [];
  const selectedDegIds = formData.degree_ids
    ? formData.degree_ids.split(",").map((s) => s.trim())
    : [];
  const selectedAccrIds = formData.accreditation_ids
    ? formData.accreditation_ids.split(",").map((s) => s.trim())
    : [];
  const selectedEstIds = (
    formData.establishment_ids
      ? String(formData.establishment_ids).split(",")
      : formData.establishment_id
        ? [String(formData.establishment_id)]
        : []
  )
    .map((s) => s.trim())
    .filter(Boolean);

  const toggleCsv = (field, id, selected) => {
    const strId = String(id);
    const updated = selected.includes(strId)
      ? selected.filter((x) => x !== strId)
      : [...selected, strId];
    onChange(field, updated.join(","));
  };

  const toggleEstablishment = (id) => {
    const strId = String(id);
    const updated = selectedEstIds.includes(strId)
      ? selectedEstIds.filter((x) => x !== strId)
      : [...selectedEstIds, strId];
    onChange("establishment_ids", updated.join(","));
    onChange("establishment_id", updated[0] || "");
  };

  const handleCountryChange = (countryId) => {
    onChange("country_id", countryId);
    onChange("location_id", "");
    onChange("location_name", "");
    onChange("state_id", "");
    onChange("city_id", "");
    const country = countries.find((c) => String(c.location_id) === String(countryId));
    onChange("country_name", country?.location_name || "");
    if (country?.country_code) onChange("country_code", country.country_code);
  };

  const handleLocationChange = (locationId) => {
    onChange("location_id", locationId);
    const loc = countryLocations.find((l) => String(l.location_id) === String(locationId));
    onChange("location_name", loc?.location_name || "");
  };

  const handleSpecialtyChange = (specialtyId) => {
    onChange("medical_specialty_id", specialtyId);
    const specialty = specialties.find(
      (s) => String(s.medical_specialty_id) === String(specialtyId)
    );
    const validProcIds = filterProceduresForSpecialty(
      medicalProcedures,
      specialtyId,
      specialty
    ).map((p) => String(p.procedure_id));
    const kept = selectedProcIds.filter((id) => validProcIds.includes(id));
    onChange("procedure_ids", kept.join(","));
  };

  const showBasic = formMode === "admin" || formMode === "doctor" || formMode === "full";
  const showProfileSections = formMode === "doctor" || formMode === "full";

  return (
    <div className="em-form">
      {showBasic && (
      <DocSectionCard title="Basic Information" sectionNumber={1}>
        {formMode === "admin" && (
          <p className="em-field-hint mb-3">
            Super Admin creates the doctor account and login. The doctor can update these details after login.
          </p>
        )}
        {formMode === "doctor" && (
          <p className="em-field-hint mb-3">
            Update your basic details below, then complete the remaining profile sections.
          </p>
        )}
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="doctorName">
            <Form.Label>Doctor Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              required
              type="text"
              placeholder="e.g. Dr. Sanjay J"
              value={formData.doctor_name || ""}
              onChange={(e) => onChange("doctor_name", e.target.value)}
            />
            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="establishmentId">
            <Form.Label>Establishment/ Hospital Name</Form.Label>
            <div className="em-accr-box">
              {establishments.length === 0 && (
                <small className="text-muted">No establishments available</small>
              )}
              {establishments.map((est) => (
                <Form.Check
                  key={est.establishment_id}
                  type="checkbox"
                  id={`doc-est-${est.establishment_id}`}
                  label={est.establishment_name}
                  checked={selectedEstIds.includes(String(est.establishment_id))}
                  onChange={() => toggleEstablishment(est.establishment_id)}
                />
              ))}
            </div>
            <span className="em-field-hint">
              Multi-select hospitals ({selectedEstIds.length} selected). These appear in
              Hospital Choice for this treating doctor.
            </span>
          </Form.Group>
        </Row>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="doctorRole">
            <Form.Label>Doctor Role (User Type)</Form.Label>
            <Form.Select
              value={formData.user_type_id || ""}
              onChange={(e) => onChange("user_type_id", e.target.value)}
            >
              <option value="">Select user type</option>
              {userTypes.map((ut) => (
                <option key={ut.user_type_id} value={ut.user_type_id}>
                  {ut.user_type}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="docCountry">
            <Form.Label>Country</Form.Label>
            <Form.Select
              value={formData.country_id || ""}
              onChange={(e) => handleCountryChange(e.target.value)}
            >
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c.location_id} value={c.location_id}>{c.location_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Row>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="docLocation">
            <Form.Label>Location</Form.Label>
            <Form.Select
              value={formData.location_id || ""}
              onChange={(e) => handleLocationChange(e.target.value)}
              disabled={!formData.country_id}
            >
              <option value="">
                {!formData.country_id ? "Select country first" : "Select location"}
              </option>
              {countryLocations.map((loc) => (
                <option key={loc.location_id} value={loc.location_id}>{loc.location_name}</option>
              ))}
            </Form.Select>
            <span className="em-field-hint">City/District locations from Location Master for selected country</span>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="docPayoutPct">
            <Form.Label>Payout %</Form.Label>
            <Form.Control
              type="number"
              placeholder="e.g. 15"
              min="0"
              max="100"
              step="0.01"
              value={formData.payout_percentage ?? ""}
              onChange={(e) => onChange("payout_percentage", e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="em-row em-row--last">
          <Form.Group as={Col} md="6" controlId="docEngagementLetter">
            <Form.Label>Upload Engagement Letter</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf,image/*"
              className="em-file-input"
              onChange={(e) => onChange("engagement_letter", e.target.files[0])}
            />
            {(formData.engagement_letter instanceof File || formData.engagement_letter_path) && (
              <span className="em-file-status">
                {formData.engagement_letter instanceof File
                  ? "Engagement letter selected ✓"
                  : (
                    <a
                      href={`${UPLOADS_BASE}/uploads/${formData.engagement_letter_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View uploaded letter
                    </a>
                  )}
              </span>
            )}
          </Form.Group>
        </Row>
      </DocSectionCard>
      )}

      {showProfileSections && (
      <>
      <DocSectionCard title="Doctor Information" sectionNumber={2}>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="medSpecialty">
            <Form.Label>Medical Speciality</Form.Label>
            <Form.Select
              value={formData.medical_specialty_id || ""}
              onChange={(e) => handleSpecialtyChange(e.target.value)}
            >
              <option value="">Select speciality</option>
              {specialties.map((s) => (
                <option key={s.medical_specialty_id} value={s.medical_specialty_id}>
                  {s.medical_specialty}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="departmentId">
            <Form.Label>Department</Form.Label>
            <Form.Select
              value={formData.department_id || ""}
              onChange={(e) => onChange("department_id", e.target.value)}
            >
              <option value="">Select department</option>
              {departments.map((d) => (
                <option key={d.department_id} value={d.department_id}>
                  {d.department_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Row>

        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="procedures">
            <Form.Label>Medical Procedures</Form.Label>
            <div className="em-accr-box">
              {!formData.medical_specialty_id && (
                <small className="text-muted">Select a medical speciality first</small>
              )}
              {formData.medical_specialty_id && filteredProcedures.length === 0 && (
                <small className="text-muted">No procedures for this speciality</small>
              )}
              {filteredProcedures.map((p) => (
                <Form.Check
                  key={p.procedure_id}
                  type="checkbox"
                  id={`proc-${p.procedure_id}`}
                  label={p.procedure_name}
                  checked={selectedProcIds.includes(String(p.procedure_id))}
                  onChange={() => toggleCsv("procedure_ids", p.procedure_id, selectedProcIds)}
                />
              ))}
            </div>
            <span className="em-field-hint">Multi-select — filtered by speciality</span>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="specializations">
            <Form.Label>Specialisation</Form.Label>
            <div className="em-accr-box">
              {specializations.length === 0 && (
                <small className="text-muted">No specialisations available</small>
              )}
              {specializations.map((s) => (
                <Form.Check
                  key={s.specialization_id}
                  type="checkbox"
                  id={`specz-${s.specialization_id}`}
                  label={s.specialization}
                  checked={selectedSpecIds.includes(String(s.specialization_id))}
                  onChange={() => toggleCsv("specialization_ids", s.specialization_id, selectedSpecIds)}
                />
              ))}
            </div>
            <span className="em-field-hint">Multi-select</span>
          </Form.Group>
        </Row>

        <Row className="em-row em-row--last">
          <Col md="6">
            <Form.Group className="mb-3" controlId="designationId">
              <Form.Label>Designation</Form.Label>
              <Form.Select
                value={formData.designation_id || ""}
                onChange={(e) => onChange("designation_id", e.target.value)}
              >
                <option value="">Select designation</option>
                {designations.map((d) => (
                  <option key={d.designation_id} value={d.designation_id}>
                    {d.designation}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3" controlId="regYear">
              <Form.Label>Registration Year</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 2010"
                min="1950"
                max={new Date().getFullYear()}
                value={formData.registration_year || ""}
                onChange={(e) => onChange("registration_year", e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="regCertificate">
              <Form.Label>Registration Certificate (Upload)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,image/*"
                className="em-file-input"
                onChange={(e) => onChange("registration_certificate", e.target.files[0])}
              />
              {(formData.registration_certificate instanceof File || formData.registration_certificate_path) && (
                <span className="em-file-status">Certificate uploaded ✓</span>
              )}
            </Form.Group>
          </Col>
          <Col md="6">
            <Form.Group className="mb-3" controlId="degrees">
              <Form.Label>Medical Degree</Form.Label>
              <div className="em-accr-box">
                {degrees.length === 0 && (
                  <small className="text-muted">No degrees available</small>
                )}
                {degrees.map((d) => (
                  <Form.Check
                    key={d.medical_degree_id}
                    type="checkbox"
                    id={`deg-${d.medical_degree_id}`}
                    label={d.medical_degree_title}
                    checked={selectedDegIds.includes(String(d.medical_degree_id))}
                    onChange={() => toggleCsv("degree_ids", d.medical_degree_id, selectedDegIds)}
                  />
                ))}
              </div>
              <span className="em-field-hint">Multi-select</span>
            </Form.Group>
            <Form.Group controlId="accrIds">
              <Form.Label>Accreditation</Form.Label>
              <div className="em-accr-box">
                {accreditations.map((a) => (
                  <Form.Check
                    key={a.accreditation_id}
                    type="checkbox"
                    id={`doc-accr-${a.accreditation_id}`}
                    label={a.accreditation_title}
                    checked={selectedAccrIds.includes(String(a.accreditation_id))}
                    onChange={() => toggleCsv("accreditation_ids", a.accreditation_id, selectedAccrIds)}
                  />
                ))}
              </div>
            </Form.Group>
          </Col>
        </Row>
      </DocSectionCard>

      <DocSectionCard title="Doctor Profile" sectionNumber={3}>
        <Row className="em-row">
          <Form.Group as={Col} md="12" controlId="shortProfile">
            <Form.Label>Short Profile</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Brief doctor profile for listings"
              value={formData.short_profile || ""}
              onChange={(e) => onChange("short_profile", e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="em-row">
          <Form.Group as={Col} md="12" controlId="longProfile">
            <Form.Label>Long Profile</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Detailed doctor profile"
              value={formData.long_profile || ""}
              onChange={(e) => onChange("long_profile", e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="profilePic">
            <Form.Label>Profile Pic (Upload)</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              className="em-file-input"
              onChange={(e) => onChange("profile_pic", e.target.files[0])}
            />
            {(formData.profile_pic instanceof File || formData.profile_pic_path) && (
              <div className="em-file-preview">
                <img
                  src={
                    formData.profile_pic instanceof File
                      ? URL.createObjectURL(formData.profile_pic)
                      : `${UPLOADS_BASE}/${formData.profile_pic_path}`
                  }
                  alt="Profile preview"
                />
              </div>
            )}
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="profilePdf">
            <Form.Label>Profile (Upload PDF)</Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              className="em-file-input"
              onChange={(e) => onChange("profile_pdf", e.target.files[0])}
            />
            {(formData.profile_pdf instanceof File || formData.profile_pdf_path) && (
              <span className="em-file-status">PDF uploaded ✓</span>
            )}
          </Form.Group>
        </Row>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="videoBio">
            <Form.Label>Video Bio (Link)</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://youtube.com/watch?v=..."
              value={formData.video_bio_url || ""}
              onChange={(e) => onChange("video_bio_url", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="3" controlId="articles">
            <Form.Label>No of Articles Published</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="0"
              value={formData.articles_published || ""}
              onChange={(e) => onChange("articles_published", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="3" controlId="surgeries">
            <Form.Label>No of Surgeries</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="0"
              value={formData.surgeries_count || ""}
              onChange={(e) => onChange("surgeries_count", e.target.value)}
            />
          </Form.Group>
        </Row>
      </DocSectionCard>

      <DocSectionCard title="Contact Information" sectionNumber={4}>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="countryCode">
            <Form.Label>Country Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="+91"
              maxLength={10}
              value={formData.country_code || ""}
              onChange={(e) => onChange("country_code", e.target.value)}
              readOnly={!!formData.country_id}
            />
            <span className="em-field-hint">Filtered from country selection</span>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="phoneNumbers">
            <Form.Label>Phone No</Form.Label>
            <DocPhoneList formData={formData} onChange={onChange} />
          </Form.Group>
        </Row>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="addressLine1">
            <Form.Label>Address Line 1</Form.Label>
            <Form.Control
              type="text"
              placeholder="Street, building, area"
              value={formData.address || ""}
              onChange={(e) => onChange("address", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="addressLine2">
            <Form.Label>Address Line 2</Form.Label>
            <Form.Control
              type="text"
              placeholder="Landmark, suite, floor"
              value={formData.address_line_2 || ""}
              onChange={(e) => onChange("address_line_2", e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="docState">
            <Form.Label>State / Province</Form.Label>
            <Form.Select
              value={formData.state_id || ""}
              onChange={(e) => {
                onChange("state_id", e.target.value);
                onChange("city_id", "");
              }}
              disabled={!formData.country_id}
            >
              <option value="">Select state</option>
              {states.map((s) => (
                <option key={s.location_id} value={s.location_id}>{s.location_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="docCity">
            <Form.Label>City</Form.Label>
            <Form.Select
              value={formData.city_id || ""}
              onChange={(e) => onChange("city_id", e.target.value)}
              disabled={!formData.country_id}
            >
              <option value="">Select city</option>
              {cities.map((c) => (
                <option key={c.location_id} value={c.location_id}>{c.location_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Row>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="longitude">
            <Form.Label>Longitude</Form.Label>
            <Form.Control
              type="number"
              step="any"
              placeholder="e.g. 77.2090"
              value={formData.longitude || ""}
              onChange={(e) => onChange("longitude", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="latitude">
            <Form.Label>Latitude</Form.Label>
            <Form.Control
              type="number"
              step="any"
              placeholder="e.g. 28.6139"
              value={formData.latitude || ""}
              onChange={(e) => onChange("latitude", e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="em-row em-row--last">
          <Form.Group as={Col} md="6" controlId="emailAddr">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              placeholder="doctor@example.com"
              value={formData.email_address || ""}
              onChange={(e) => onChange("email_address", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="website">
            <Form.Label>Website Address</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://example.com"
              value={formData.website_address || ""}
              onChange={(e) => onChange("website_address", e.target.value)}
            />
          </Form.Group>
        </Row>
      </DocSectionCard>

      <DocSectionCard title="Social Media Profile" sectionNumber={5}>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="facebook">
            <Form.Label>Facebook Page</Form.Label>
            <Form.Control
              type="text"
              placeholder="Facebook URL"
              value={formData.facebook_page || ""}
              onChange={(e) => onChange("facebook_page", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="youtube">
            <Form.Label>YouTube Channel Link</Form.Label>
            <Form.Control
              type="text"
              placeholder="YouTube URL"
              value={formData.youtube_channel || ""}
              onChange={(e) => onChange("youtube_channel", e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="em-row">
          <Form.Group as={Col} md="6" controlId="instagram">
            <Form.Label>Instagram Page</Form.Label>
            <Form.Control
              type="text"
              placeholder="Instagram URL"
              value={formData.instagram || ""}
              onChange={(e) => onChange("instagram", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="linkedin">
            <Form.Label>LinkedIn Page</Form.Label>
            <Form.Control
              type="text"
              placeholder="LinkedIn URL"
              value={formData.linkedin || ""}
              onChange={(e) => onChange("linkedin", e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="em-row em-row--last">
          <Form.Group as={Col} md="6" controlId="xProfile">
            <Form.Label>X-Twitter Handle</Form.Label>
            <Form.Control
              type="text"
              placeholder="X (Twitter) URL or handle"
              value={formData.x_profile || ""}
              onChange={(e) => onChange("x_profile", e.target.value)}
            />
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="telegram">
            <Form.Label>Telegram / WhatsApp Channel</Form.Label>
            <Form.Control
              type="text"
              placeholder="Telegram or WhatsApp link"
              value={formData.telegram_channel || ""}
              onChange={(e) => onChange("telegram_channel", e.target.value)}
            />
          </Form.Group>
        </Row>
      </DocSectionCard>
      </>
      )}
    </div>
  );
};

export default DoctorProfileForm;
